import React, { useState, useRef, useEffect } from 'react';
import { Send, ThumbsUp, ThumbsDown, Copy, Loader2, MessageSquare, X, Network, MessageCircle, AlertCircle, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useData } from '../context/DataContext';
import * as d3 from 'd3';

// Feedback Modal Component
const FeedbackModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (feedback: string) => void;
  messageContent: string;
}> = ({ isOpen, onClose, onSubmit, messageContent }) => {
  const [feedback, setFeedback] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (feedback.trim()) {
      onSubmit(feedback);
      setFeedback('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 20 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="bg-white rounded-2xl p-6 w-full max-w-md mx-4 border border-gray-200 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-black">Provide Feedback</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-black transition-colors p-1 hover:bg-gray-100 rounded-lg"
          >
            <X size={20} />
          </button>
        </div>
        
        <div className="mb-4">
          <p className="text-sm font-semibold text-gray-700 mb-2">Response:</p>
          <div className="bg-gray-50 p-3 rounded-lg text-sm text-gray-700 max-h-32 overflow-y-auto border border-gray-200">
            {messageContent}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="What could be improved about this response?"
            className="w-full p-3 bg-white border-2 border-gray-200 rounded-lg text-gray-700 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none transition-all"
            rows={4}
            required
          />
          <div className="flex justify-end gap-3 mt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-black hover:bg-gray-100 rounded-lg transition-all font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg transition-all font-semibold shadow-lg"
            >
              Submit Feedback
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

const ChatInterface: React.FC = () => {
  const [inputMessage, setInputMessage] = useState<string>('');
  const [messageRatings, setMessageRatings] = useState<Record<number, 'up' | 'down' | null>>({});
  const [feedbackModal, setFeedbackModal] = useState<{
    isOpen: boolean;
    messageIndex: number;
    messageContent: string;
  }>({
    isOpen: false,
    messageIndex: -1,
    messageContent: ''
  });
  const [showKG, setShowKG] = useState<boolean>(false);
  const [messageType, setMessageType] = useState<'ask' | 'feedback'>('ask');
  
  const { 
    chatHistory, 
    sendMessage, 
    isLoading, 
    sections, 
    submitRating, 
    submitFeedback,
    generateChatKG,
    chatKG,
    setChatKG
  } = useData();
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const kgSvgRef = useRef<SVGSVGElement | null>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory]);

  const handleSendMessage = async (e?: React.FormEvent, type: 'ask' | 'feedback' = messageType) => {
    e?.preventDefault();
    if (inputMessage.trim() && !isLoading) {
      if (type === 'ask') {
        await sendMessage(inputMessage);
      } else {
        // For feedback, we send it as a correction/suggestion
        await submitFeedback(chatHistory.length - 1, inputMessage, 'User feedback for improvement');
      }
      setInputMessage('');
      setMessageType('ask'); // Reset to ask mode
    }
  };

  const handleSuggestedQuestion = (question: string) => {
    setInputMessage(question);
  };

  const handleRating = async (messageIndex: number, rating: 'up' | 'down') => {
    const newRating = messageRatings[messageIndex] === rating ? null : rating;
    setMessageRatings(prev => ({
      ...prev,
      [messageIndex]: newRating
    }));
    
    // Submit rating to backend
    if (newRating) {
      await submitRating(messageIndex, newRating);
    }
  };

  const handleFeedback = (messageIndex: number, messageContent: string) => {
    setFeedbackModal({
      isOpen: true,
      messageIndex,
      messageContent
    });
  };

  const handleFeedbackSubmit = async (feedback: string) => {
    await submitFeedback(feedbackModal.messageIndex, feedback, feedbackModal.messageContent);
    setFeedbackModal({ isOpen: false, messageIndex: -1, messageContent: '' });
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    // You could add a toast notification here
  };

  // Count user questions (only count user messages)
  const userQuestionCount = chatHistory.filter(msg => msg.role === 'user').length;
  const canGenerateKG = userQuestionCount >= 10;

  const handleGenerateKG = async () => {
    if (canGenerateKG) {
      console.log('🚀 Starting KG generation...');
      console.log('📝 Chat history:', chatHistory);
      
      // API connectivity is handled by the context
      
      try {
        // For debugging: try creating a simple KG first
        console.log('🧪 Testing with simple KG creation...');
        const testKG = {
          nodes: [
            { id: 'test1', name: 'Test Node 1', type: 'concept', size: 15, color: '#06b6d4' },
            { id: 'test2', name: 'Test Node 2', type: 'concept', size: 12, color: '#10b981' }
          ],
          edges: [
            { source: 'test1', target: 'test2', relation: 'related', color: '#666666', weight: 1 }
          ]
        };
        console.log('🧪 Test KG created:', testKG);
        
        const result = await generateChatKG(chatHistory);
        console.log('📊 Generated KG result:', result);
        
        console.log('📊 Full result object:', JSON.stringify(result, null, 2));
        console.log('📊 Result type:', typeof result);
        console.log('📊 Result keys:', result ? Object.keys(result) : 'null');
        console.log('📊 Has nodes property:', result && 'nodes' in result);
        console.log('📊 Nodes array:', result?.nodes);
        console.log('📊 Nodes length:', result?.nodes?.length);
        console.log('📊 Result is null:', result === null);
        console.log('📊 Result is undefined:', result === undefined);
        console.log('📊 Result is empty object:', result && Object.keys(result).length === 0);
        
        if (result && result.nodes && Array.isArray(result.nodes) && result.nodes.length > 0) {
          setShowKG(true);
          console.log('✅ KG generated successfully, showing KG view');
        } else {
          console.log('❌ Failed to generate KG - no valid data returned');
          console.log('📊 Result structure analysis:');
          console.log('- result exists:', !!result);
          console.log('- result.nodes exists:', !!(result && result.nodes));
          console.log('- result.nodes is array:', !!(result && Array.isArray(result.nodes)));
          console.log('- result.nodes length:', result?.nodes?.length || 0);
          console.log('- result.edges exists:', !!(result && result.edges));
          console.log('- result.edges length:', result?.edges?.length || 0);
          
          // Try to show KG even with minimal data
          if (result && (result.nodes || result.edges)) {
            console.log('🔄 Attempting to show KG with available data...');
            setShowKG(true);
          } else {
            console.log('🔄 Creating fallback KG from chat content...');
            // Create a simple fallback KG from chat content
            const fallbackKG = createFallbackKG(chatHistory);
            console.log('📊 Fallback KG created:', fallbackKG);
            setChatKG(fallbackKG);
            setShowKG(true);
          }
        }
      } catch (error) {
        console.error('❌ Error generating KG:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        alert(`Error generating knowledge graph: ${errorMessage}. Check console for details.`);
      }
    }
  };

  const closeKG = () => {
    setShowKG(false);
  };

  const createFallbackKG = (chatHistory: Array<{role: string, content: string}>) => {
    console.log('🔄 Creating fallback KG from chat history...');
    
    // Extract all text from chat
    const allText = chatHistory.map(msg => msg.content).join(' ').toLowerCase();
    
    // Simple word extraction
    const words = allText.split(/\s+/).filter(word => 
      word.length > 3 && 
      !['the', 'and', 'for', 'are', 'but', 'not', 'you', 'all', 'can', 'had', 'her', 'was', 'one', 'our', 'out', 'day', 'get', 'has', 'him', 'his', 'how', 'its', 'may', 'new', 'now', 'old', 'see', 'two', 'way', 'who', 'boy', 'did', 'man', 'oil', 'sit', 'try', 'use', 'why', 'ask', 'came', 'each', 'eat', 'end', 'far', 'few', 'got', 'let', 'put', 'say', 'she', 'too', 'use'].includes(word)
    );
    
    // Count word frequency
    const wordCounts: Record<string, number> = {};
    words.forEach(word => {
      wordCounts[word] = (wordCounts[word] || 0) + 1;
    });
    
    // Get top 8 most frequent words
    const topWords = Object.entries(wordCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 8)
      .map(([word]) => word);
    
    console.log('📊 Top words found:', topWords);
    
    // Create nodes
    const nodes = topWords.map((word, index) => ({
      id: `word_${index}`,
      name: word.charAt(0).toUpperCase() + word.slice(1),
      type: 'concept',
      size: Math.min(wordCounts[word] * 2 + 8, 20),
      color: '#3b82f6'
    }));
    
    // Create simple edges
    const edges = [];
    for (let i = 0; i < nodes.length - 1; i++) {
      edges.push({
        source: nodes[i].id,
        target: nodes[i + 1].id,
        relation: 'related',
        weight: 1
      });
    }
    
    const fallbackKG = { nodes, edges };
    console.log('✅ Fallback KG created:', fallbackKG);
    return fallbackKG;
  };

  // Render KG when chatKG data changes
  useEffect(() => {
    console.log('🔄 useEffect triggered:', { showKG, chatKG: !!chatKG, svgRef: !!kgSvgRef.current });
    if (showKG && chatKG && kgSvgRef.current) {
      console.log('🎨 Rendering KG with data:', chatKG);
      // Add a small delay to ensure SVG is properly mounted
      setTimeout(() => {
        renderChatKG();
      }, 100);
    }
  }, [showKG, chatKG]);

  const renderChatKG = () => {
    console.log('🎨 renderChatKG called');
    console.log('📊 chatKG data:', chatKG);
    console.log('🖼️ svgRef current:', kgSvgRef.current);
    
    if (!chatKG || !kgSvgRef.current) {
      console.log('❌ Missing data or SVG ref');
      return;
    }

    const svg = d3.select(kgSvgRef.current);
    svg.selectAll('*').remove();

    const width = kgSvgRef.current.clientWidth;
    const height = kgSvgRef.current.clientHeight;
    
    console.log('📏 SVG dimensions:', { width, height });

    // Ensure we have valid nodes and edges arrays
    const nodes = Array.isArray(chatKG.nodes) ? chatKG.nodes : [];
    const edges = Array.isArray(chatKG.edges) ? chatKG.edges : [];
    
    console.log('📊 Validated nodes:', nodes);
    console.log('📊 Validated edges:', edges);
    
    if (nodes.length === 0) {
      console.log('⚠️ No nodes to display, creating a simple placeholder');
      // Create a simple placeholder node
      const placeholderNode = {
        id: 'placeholder',
        name: 'No data available',
        type: 'concept',
        size: 20,
        color: '#666666'
      };
      nodes.push(placeholderNode);
    }

    // Set explicit SVG dimensions if they're 0
    if (width === 0 || height === 0) {
      console.log('⚠️ SVG has zero dimensions, setting defaults');
      svg.attr('width', 600).attr('height', 400);
      const newWidth = 600;
      const newHeight = 400;
      
      const simulation = d3.forceSimulation(nodes as any)
        .force('link', d3.forceLink(edges).id((d: any) => d.id).distance(100))
        .force('charge', d3.forceManyBody().strength(-300))
        .force('center', d3.forceCenter(newWidth / 2, newHeight / 2))
        .force('collision', d3.forceCollide().radius(30));
      
      console.log('🔗 Creating links and nodes with default dimensions');
      createGraphElements(svg, simulation, nodes, edges, newWidth, newHeight);
    } else {
      const simulation = d3.forceSimulation(nodes as any)
        .force('link', d3.forceLink(edges).id((d: any) => d.id).distance(100))
        .force('charge', d3.forceManyBody().strength(-300))
        .force('center', d3.forceCenter(width / 2, height / 2))
        .force('collision', d3.forceCollide().radius(30));
      
      console.log('🔗 Creating links and nodes with calculated dimensions');
      createGraphElements(svg, simulation, nodes, edges, width, height);
    }
  };

  const createGraphElements = (svg: any, simulation: any, nodes: any[], edges: any[], width: number, height: number) => {
    console.log('🎨 Creating graph elements with dimensions:', { width, height });
    console.log('📊 Nodes count:', nodes.length);
    console.log('📊 Edges count:', edges.length);
    console.log('📊 Validated nodes:', nodes);
    console.log('📊 Validated edges:', edges);

    // Create links
    const link = svg.append('g')
      .selectAll('line')
      .data(edges)
      .enter().append('line')
      .attr('stroke', '#94a3b8')
      .attr('stroke-opacity', 0.6)
      .attr('stroke-width', 1.5);

    // Create nodes
    const node = svg.append('g')
      .selectAll('circle')
      .data(nodes)
      .enter().append('circle')
      .attr('r', (d: any) => Math.max(6, d.size || 10))
      .attr('fill', (d: any) => d.color || '#3b82f6')
      .attr('stroke', '#ffffff')
      .attr('stroke-width', 2)
      .style('cursor', 'pointer')
      .call(d3.drag<SVGCircleElement, any>()
        .on('start', (event, d: any) => {
          if (!event.active) simulation.alphaTarget(0.3).restart();
          d.fx = d.x;
          d.fy = d.y;
        })
        .on('drag', (event, d: any) => {
          d.fx = event.x;
          d.fy = event.y;
        })
        .on('end', (event, d: any) => {
          if (!event.active) simulation.alphaTarget(0);
          d.fx = null;
          d.fy = null;
        }));

    // Create labels
    const label = svg.append('g')
      .selectAll('text')
      .data(nodes)
      .enter().append('text')
      .text((d: any) => d.label)
      .attr('font-size', '10px')
      .attr('fill', '#ffffff')
      .attr('text-anchor', 'middle')
      .attr('dy', '0.35em')
      .style('pointer-events', 'none')
      .style('user-select', 'none');

    // Update positions
    simulation.on('tick', () => {
      link
        .attr('x1', (d: any) => d.source.x)
        .attr('y1', (d: any) => d.source.y)
        .attr('x2', (d: any) => d.target.x)
        .attr('y2', (d: any) => d.target.y);

      node
        .attr('cx', (d: any) => d.x)
        .attr('cy', (d: any) => d.y);

      label
        .attr('x', (d: any) => d.x)
        .attr('y', (d: any) => d.y + ((d.size || 10) + 15));
    });

    console.log('✅ Graph elements created successfully');
  };

  const suggestedQuestions = [
    "What is this document about?",
    "Summarize the key findings.",
    "What are the main sections?",
    "Tell me about the methodology.",
    "What are the safety precautions?",
  ];

  return (
    <div className="h-full w-full flex flex-col bg-white">
      {/* Full-page Chat Container */}
      <div className={`flex-1 flex ${showKG ? 'gap-0' : ''} h-full overflow-hidden`}>
        {/* Chat Section - Full Width */}
        <div className={`flex flex-col ${showKG ? 'w-1/2 border-r border-gray-200' : 'w-full'} h-full bg-white`}>
          {/* Minimal Top Bar */}
          <div className="flex-shrink-0 px-6 py-4 border-b border-gray-100 bg-white/95 backdrop-blur-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {userQuestionCount > 0 && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg"
                  >
                    <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
                    <span className="text-xs font-medium text-gray-700">
                      {userQuestionCount}/10
                    </span>
                  </motion.div>
                )}
                {userQuestionCount >= 10 && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="px-3 py-1.5 bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 rounded-lg text-xs font-semibold border border-green-200"
                  >
                    ✓ Ready for KG
                  </motion.span>
                )}
              </div>
              
              {canGenerateKG && !showKG && (
                <motion.button
                  onClick={handleGenerateKG}
                  disabled={isLoading}
                  className={`flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg font-medium text-sm shadow-sm transition-all ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  whileHover={{ scale: isLoading ? 1 : 1.02 }}
                  whileTap={{ scale: isLoading ? 1 : 0.98 }}
                >
                  {isLoading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      <span>Generating...</span>
                    </>
                  ) : (
                    <>
                      <Network size={16} />
                      <span>Generate KG</span>
                    </>
                  )}
                </motion.button>
              )}
            </div>
          </div>

      {!sections || sections.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex-1 flex items-center justify-center text-center px-6"
        >
          <div className="max-w-md">
            <div className="mb-6 flex justify-center">
              <div className="w-16 h-16 bg-gray-50 rounded-xl flex items-center justify-center">
                <MessageSquare className="w-8 h-8 text-gray-400" />
              </div>
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">No Documents Yet</h3>
            <p className="text-sm text-gray-500">Upload a document to start chatting.</p>
          </div>
        </motion.div>
      ) : (
        <>
          {/* Messages Container - Takes full available space */}
          <div className="flex-1 overflow-y-auto px-6 py-6 custom-scrollbar">
            <AnimatePresence initial={false}>
              {chatHistory.length === 0 && !isLoading && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  className="mb-8"
                >
                  <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-50 rounded-xl mb-4">
                      <MessageSquare className="w-6 h-6 text-gray-400" />
                    </div>
                    <h3 className="text-base font-medium text-gray-700 mb-1">Start a conversation</h3>
                    <p className="text-xs text-gray-400">Choose a question below or type your own</p>
                  </div>
                  <div className="flex flex-wrap gap-2 justify-center max-w-2xl mx-auto">
                    {suggestedQuestions.map((q, index) => (
                      <motion.button
                        key={index}
                        className="px-4 py-2 bg-gray-50 border border-gray-200 hover:border-purple-300 hover:bg-purple-50 text-gray-700 hover:text-purple-700 rounded-lg text-sm font-normal transition-all duration-200"
                        onClick={() => handleSuggestedQuestion(q)}
                        whileHover={{ scale: 1.02, y: -1 }}
                        whileTap={{ scale: 0.98 }}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ 
                          delay: index * 0.05,
                          type: "spring",
                          stiffness: 300,
                          damping: 25
                        }}
                      >
                        {q}
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
              )}

              {chatHistory.map((msg, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ 
                    duration: 0.3,
                    delay: index === chatHistory.length - 1 ? 0.05 : 0
                  }}
                  className={`flex mb-6 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[80%] md:max-w-[75%] flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                    {/* Avatar */}
                    <div className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-xs font-medium ${
                      msg.role === 'user' 
                        ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {msg.role === 'user' ? 'U' : 'AI'}
                    </div>
                    
                    {/* Message Bubble */}
                    <div className={`flex flex-col gap-1.5 ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                      <div className={`px-4 py-3 rounded-xl ${
                        msg.role === 'user'
                          ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
                          : 'bg-gray-50 border border-gray-200 text-gray-800'
                      }`}>
                        <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                          {msg.content}
                        </p>
                      </div>
                      {/* Citations */}
                      {msg.citations && msg.citations.length > 0 && (
                        <div className="mt-2 px-3 py-2 bg-purple-50/50 border border-purple-100 rounded-lg">
                          <p className="text-xs font-medium text-purple-700 mb-1.5 flex items-center gap-1.5">
                            <span>📚</span> Sources:
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {msg.citations.map((citation, idx) => (
                              <span
                                key={idx}
                                className="px-2 py-0.5 bg-white border border-purple-200 rounded text-xs text-purple-600 hover:bg-purple-50 transition-colors"
                                title={typeof citation === 'string' ? citation : citation.title}
                              >
                                {typeof citation === 'string' ? citation : citation.title}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Action Buttons */}
                      {msg.role === 'assistant' && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.1 }}
                          className="flex items-center gap-1 mt-1.5"
                        >
                          <motion.button
                            onClick={() => handleCopy(msg.content)}
                            className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            title="Copy message"
                          >
                            <Copy size={14} />
                          </motion.button>
                          
                          <motion.button
                            onClick={() => handleRating(index, 'up')}
                            className={`p-1.5 rounded-md transition-colors ${
                              messageRatings[index] === 'up' 
                                ? 'bg-green-50 text-green-600' 
                                : 'hover:bg-gray-100 text-gray-500 hover:text-green-600'
                            }`}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            title="Good answer"
                          >
                            <ThumbsUp size={14} />
                          </motion.button>
                          
                          <motion.button
                            onClick={() => handleRating(index, 'down')}
                            className={`p-1.5 rounded-md transition-colors ${
                              messageRatings[index] === 'down' 
                                ? 'bg-red-50 text-red-600' 
                                : 'hover:bg-gray-100 text-gray-500 hover:text-red-600'
                            }`}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            title="Poor answer"
                          >
                            <ThumbsDown size={14} />
                          </motion.button>
                          
                          <motion.button
                            onClick={() => handleFeedback(index, msg.content)}
                            className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 hover:text-orange-600 transition-colors"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            title="Provide feedback"
                          >
                            <MessageSquare size={14} />
                          </motion.button>
                        </motion.div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
              {isLoading && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ type: "spring", stiffness: 300, damping: 25 }}
                  className="flex justify-start mb-5"
                >
                  <div className="max-w-[75%] md:max-w-[70%] p-4 md:p-5 rounded-2xl rounded-bl-md shadow-xl bg-white border-2 border-purple-100 flex items-center gap-3">
                    <div className="relative">
                      <Loader2 className="animate-spin text-purple-600" size={20} />
                      <div className="absolute inset-0 animate-ping opacity-30">
                        <Loader2 className="text-purple-600" size={20} />
                      </div>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-800 font-semibold">Thinking...</span>
                      <span className="text-xs text-gray-500">Analyzing your question</span>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area - Fixed at Bottom */}
          <div className="flex-shrink-0 border-t border-gray-100 bg-white px-6 py-4">
            <form onSubmit={(e) => handleSendMessage(e, messageType)}>
              <div className="flex items-end gap-3 max-w-4xl mx-auto">
                {/* Input Field */}
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    placeholder={messageType === 'ask' ? "Ask a question..." : "Provide feedback..."}
                    className="w-full px-4 py-3 pr-12 bg-gray-50 border border-gray-200 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm transition-all"
                    disabled={isLoading}
                  />
                  {/* Mode Indicator Badge */}
                  {inputMessage.trim() && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                    >
                      <div className={`px-2 py-0.5 rounded-md text-xs font-medium ${
                        messageType === 'ask' 
                          ? 'bg-purple-100 text-purple-700' 
                          : 'bg-orange-100 text-orange-700'
                      }`}>
                        {messageType === 'ask' ? 'Ask' : 'Feedback'}
                      </div>
                    </motion.div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* Toggle Mode Button */}
                  <motion.button
                    type="button"
                    onClick={() => setMessageType(messageType === 'ask' ? 'feedback' : 'ask')}
                    className="p-2.5 rounded-lg border border-gray-200 hover:border-purple-300 bg-white hover:bg-purple-50 text-gray-600 hover:text-purple-600 transition-all"
                    disabled={isLoading}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    title={`Switch to ${messageType === 'ask' ? 'Feedback' : 'Ask'} mode`}
                  >
                    {messageType === 'ask' ? (
                      <MessageCircle size={18} className="text-purple-600" />
                    ) : (
                      <AlertCircle size={18} className="text-orange-600" />
                    )}
                  </motion.button>

                  {/* Send Button */}
                  <motion.button
                    type="submit"
                    disabled={isLoading || !inputMessage.trim()}
                    className={`px-5 py-3 rounded-xl font-medium text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ${
                      messageType === 'ask'
                        ? 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white shadow-sm'
                        : 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white shadow-sm'
                    }`}
                    whileHover={{ scale: isLoading || !inputMessage.trim() ? 1 : 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    title={messageType === 'ask' ? 'Ask question' : 'Submit feedback'}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <Send size={16} />
                        <span>Send</span>
                      </>
                    )}
                  </motion.button>
                </div>
              </div>
            </form>
          </div>
        </>
      )}
        </div>
      </div>

      {/* Knowledge Graph Section */}
      {showKG && (
        <motion.div 
          className="w-1/2 flex flex-col h-full bg-white border-l border-gray-200"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 20 }}
          transition={{ duration: 0.3 }}
        >
          {/* KG Header */}
          <div className="flex-shrink-0 px-6 py-4 border-b border-gray-100 bg-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg">
                  <Network size={18} className="text-white" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Chat Knowledge Graph</h3>
                  <p className="text-xs text-gray-500">Entities and relationships from your conversation</p>
                </div>
              </div>
              <button
                onClick={closeKG}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-400 hover:text-gray-600"
                title="Close Knowledge Graph"
              >
                <X size={18} />
              </button>
            </div>
          </div>
          
          {/* KG Visualization Container */}
          <div className="flex-1 bg-gray-50 overflow-hidden relative">
            {chatKG ? (
              <div className="relative h-full">
                <svg ref={kgSvgRef} className="w-full h-full" />
                
                {/* KG Info Overlay */}
                <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm rounded-lg px-3 py-2 border border-gray-200 shadow-sm">
                  <div className="flex items-center gap-4 text-xs text-gray-700">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <span className="font-medium">{chatKG.nodes?.length || 0} entities</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                      <span className="font-medium">{chatKG.edges?.length || 0} connections</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                <div className="text-center">
                  <Loader2 size={24} className="animate-spin text-purple-600 mx-auto mb-3" />
                  <p className="text-sm font-medium text-gray-700">Generating knowledge graph...</p>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={feedbackModal.isOpen}
        onClose={() => setFeedbackModal({ isOpen: false, messageIndex: -1, messageContent: '' })}
        onSubmit={handleFeedbackSubmit}
        messageContent={feedbackModal.messageContent}
      />
    </div>
  );
};

export default ChatInterface;
